import { useEffect, useState } from 'react';
import type { Webhook } from '@comms/shared';
import { api } from '../../lib/api';
import { Button, ErrorText, Input, Spinner } from '../ui';

/** Incoming webhooks for a channel. Secret URLs are only shown right after create/reset. */
export function WebhooksPanel({ channelId }: { channelId: string }) {
  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState<{ id: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = () =>
    api<Webhook[]>('GET', `/channels/${channelId}/webhooks`).then(setHooks, (err: unknown) => {
      setError(err);
      setHooks([]);
    });
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  const reveal = (w: Webhook) => {
    setCopied(false);
    setRevealed({ id: w.id, url: `${location.origin}${w.url}` });
  };

  async function create() {
    try {
      const w = await api<Webhook>('POST', `/channels/${channelId}/webhooks`, { name });
      setName('');
      reveal(w);
      await load();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-3">
        Webhooks let other apps and scripts post messages here with a secret URL (for example, build
        notifications). Anyone with the URL can post, so treat it like a password. Webhooks can't
        ping @everyone or roles.
      </p>
      <div className="flex items-end gap-2">
        <Input
          label="New webhook name"
          value={name}
          maxLength={80}
          placeholder="Build bot"
          onChange={(e) => setName(e.target.value)}
        />
        <Button disabled={!name.trim()} onClick={create}>
          Create webhook
        </Button>
      </div>
      <ErrorText error={error} />
      {revealed && (
        <div className="space-y-2 rounded border border-warning/40 bg-warning/10 p-3">
          <p className="text-sm text-text-1">
            Copy this URL now. It won't be shown again (you can reset it later).
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              aria-label="Webhook URL"
              value={revealed.url}
              onFocus={(e) => e.target.select()}
            />
            <Button
              onClick={() =>
                void navigator.clipboard.writeText(revealed.url).then(() => setCopied(true))
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-text-3">
            Post with:{' '}
            <code>curl -H 'content-type: application/json' -d '{'{"content":"hi"}'}' URL</code>
          </p>
        </div>
      )}
      {!hooks && <Spinner />}
      {hooks?.length === 0 && <p className="text-sm text-text-3">No webhooks yet.</p>}
      {hooks?.map((w) => (
        <div key={w.id} className="flex items-center gap-3 rounded bg-bg-1 p-3">
          <div className="flex-1">
            <div className="font-medium text-text-0">{w.name}</div>
            <div className="text-xs text-text-3">
              created {new Date(w.createdAt).toLocaleDateString()}
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              const next = prompt('Webhook name', w.name);
              if (next?.trim() && next !== w.name)
                void api('PATCH', `/webhooks/${w.id}`, { name: next.trim() }).then(load, setError);
            }}
          >
            Rename
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              confirm('Reset the URL? The old one stops working immediately.') &&
              void api<Webhook>('POST', `/webhooks/${w.id}/token`).then(reveal, setError)
            }
          >
            Reset URL
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              confirm(`Delete webhook "${w.name}"?`) &&
              void api('DELETE', `/webhooks/${w.id}`).then(load, setError)
            }
          >
            Delete
          </Button>
        </div>
      ))}
    </div>
  );
}
